// src/pages/Notifications/NotificationsController.ts
import {useState, useEffect} from 'react'
import {
    getNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteReadNotifications,
    getUnreadCount
} from '../../services/Notifications'
import {Notification, FilterType} from './NotificationsModel'

export const useNotificationsController = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [filter, setFilter] = useState<FilterType>('all')
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [loading, setLoading] = useState(true)
    const [unreadCount, setUnreadCount] = useState(0)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    useEffect(() => {
        fetchNotifications()
    }, [filter, page])

    useEffect(() => {
        fetchUnreadCount()
        const interval = setInterval(fetchUnreadCount, 30000)
        return () => clearInterval(interval)
    }, [])

    const fetchNotifications = async () => {
        setLoading(true)
        try {
            const response = await getNotifications({
                filter,
                limit: 20,
                offset: (page - 1) * 20
            })

            if (page === 1) {
                setNotifications(response.notifications)
            } else {
                setNotifications(prev => [...prev, ...response.notifications])
            }

            setTotal(response.total)
            setUnreadCount(response.unreadCount)
            setHasMore(response.hasMore)
        } catch (error) {
            console.error('Error fetching notifications:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchUnreadCount = async () => {
        try {
            const count = await getUnreadCount()
            setUnreadCount(count)
        } catch (error) {
            console.error('Error fetching unread count:', error)
        }
    }

    const handleMarkAsRead = async (id: number) => {
        const success = await markAsRead(id)
        if (success) {
            setNotifications(prev =>
                prev.map(n => n.id === id ? {...n, is_read: 1} : n)
            )
            setUnreadCount(prev => Math.max(0, prev - 1))
        }
    }

    const handleMarkAllAsRead = async () => {
        const success = await markAllAsRead()
        if (success) {
            setNotifications(prev => prev.map(n => ({...n, is_read: 1})))
            setUnreadCount(0)
        }
    }

    const handleDelete = async (id: number) => {
        const success = await deleteNotification(id)
        if (success) {
            const wasUnread = notifications.find(n => n.id === id)?.is_read === 0
            setNotifications(prev => prev.filter(n => n.id !== id))
            setTotal(prev => prev - 1)
            if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1))
        }
    }

    const handleDeleteRead = async () => {
        const success = await deleteReadNotifications()
        if (success) {
            setNotifications(prev => prev.filter(n => n.is_read === 0))
            setTotal(prev => prev - (total - unreadCount))
        }
    }

    const loadMore = () => {
        if (!loading && hasMore) {
            setPage(prev => prev + 1)
        }
    }

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen)
    }

    const handleFilterChange = (newFilter: FilterType) => {
        setFilter(newFilter)
        setPage(1)
    }

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return 'success'
            case 'warning': return 'warning'
            case 'info': return 'info'
            default: return 'info'
        }
    }

    const getBorderColor = (type: string) => {
        switch (type) {
            case 'success': return 'border-green-500'
            case 'warning': return 'border-yellow-500'
            case 'info': return 'border-blue-500'
            default: return 'border-gray-500'
        }
    }

    return {
        // State
        sidebarOpen,
        filter,
        notifications,
        loading,
        unreadCount,
        total,
        page,
        hasMore,

        // Actions
        toggleSidebar,
        handleFilterChange,
        handleMarkAsRead,
        handleMarkAllAsRead,
        handleDelete,
        handleDeleteRead,
        loadMore,
        getIcon,
        getBorderColor
    }
}